import React, { PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api.tsx';
import { GetWebAppAlertResponse } from '@api/ringil-api.v2.tsx';
import { createLogger } from '@utils/logger';
import { isProduction } from '@utils/utils.tsx';
import NewVersionBanner from '@components/appMessages/NewVersionBanner.tsx';
import { useAuthReady } from '@hooks/useAuthReady';

const log = createLogger('AppMessages');

const POLL_INTERVAL_MS = 1000 * 60;

// Manual triggers for exercising the banners locally — a real deploy or a backend alert is
// otherwise needed to see either of them. Ignored in production builds; documented in README.md
// under "Top-of-screen messages".
//   localStorage.setItem('dev.newVersion', '1')       -> version bar appears within ~2s
//   localStorage.setItem('dev.alert', 'Some notice')  -> maintenance strip appears within ~2s
//   localStorage.removeItem('dev.alert')              -> strip disappears within ~2s
export const DEV_NEW_VERSION_KEY = 'dev.newVersion';
const DEV_ALERT_KEY = 'dev.alert';
const DEV_POLL_MS = 2000;

interface ApplicationInfo {
  commit_sha: string;
  commit_message: string;
}

interface AppMessagesContextType {
  /** Maintenance / incident notices, exactly as returned by the API. */
  alerts: GetWebAppAlertResponse[];
  /** Set once a newer build is detected, and never cleared — only a reload removes it. */
  newVersion: ApplicationInfo | null;
}

const AppMessagesContext = React.createContext<AppMessagesContextType>({ alerts: [], newVersion: null });

export const useAppMessages = () => useContext(AppMessagesContext);

/**
 * Owns the two app-wide top-of-screen messages and the single poll that feeds them.
 *
 * Mounted in _app.tsx above the router outlet, which is the point of the whole component: the
 * previous implementation kept this state in a hook called from each layout, so navigating to
 * another page mounted a fresh layout, reset the baseline commit SHA to the *new* build, and the
 * "new version" banner could never fire again — users stayed on the old bundle indefinitely. Living
 * above the outlet, this never remounts on navigation.
 */
export const AppMessagesProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { getWebAppAlert } = useContext(ApiContext);
  const authReady = useAuthReady();
  const [alerts, setAlerts] = useState<GetWebAppAlertResponse[]>([]);
  // Kept out of `alerts` so the 60s poll overwriting real alerts cannot wipe it.
  const [devAlertText, setDevAlertText] = useState<string | null>(null);
  const [newVersion, setNewVersion] = useState<ApplicationInfo | null>(null);

  // The build this session started on. A ref, not state, so no render path can re-baseline it —
  // that re-baselining is exactly what used to swallow the notification.
  const baselineSha = useRef<string | null>(null);

  const poll = useCallback(
    (controller: AbortController) => {
      fetch('/version.json', { signal: controller.signal })
        .then(r => r.json())
        .then((data: ApplicationInfo) => {
          if (controller.signal.aborted || !data?.commit_sha) return;
          if (baselineSha.current === null) {
            baselineSha.current = data.commit_sha;
            return;
          }
          // Once set, never unset: the banner may only be resolved by reloading.
          if (data.commit_sha !== baselineSha.current) setNewVersion(prev => prev ?? data);
        })
        .catch(e => {
          if (!controller.signal.aborted) log.debug('version.json poll failed (suppressed)', e);
        });

      // version.json above is public and polls from the first render; the alert endpoint is not, so
      // it waits for a session. Without the gate the first poll fires with no bearer, and the 401
      // arriving after Auth0 finished looks like an expired token. (Sentry RINGIL-APP-WM.)
      if (!authReadyRef.current) return;
      getWebAppAlert(
        d => {
          if (!controller.signal.aborted) setAlerts(d ?? []);
        },
        undefined,
        {
          // Non-critical background poll on every page — a failed poll has no user value, so it is
          // logged rather than raising the global error toast.
          onError: error => log.debug('getWebAppAlert poll failed (suppressed)', error),
        },
      );
    },
    [getWebAppAlert],
  );

  // The interval is set up once, but calls through a ref rather than capturing `poll` directly:
  // `getWebAppAlert` gets a new identity whenever the API provider re-renders, so depending on it
  // would tear down and restart the poller constantly, while capturing it would go stale.
  const pollRef = useRef(poll);
  pollRef.current = poll;
  // Read through a ref for the same reason: the interval is created once and must see the current
  // auth state, not the one captured when it was set up.
  const authReadyRef = useRef(authReady);
  authReadyRef.current = authReady;

  useEffect(() => {
    const controller = new AbortController();
    pollRef.current(controller);
    const interval = setInterval(() => pollRef.current(controller), POLL_INTERVAL_MS);
    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, []);

  // Login completed after the first poll ran: fetch the alerts right away instead of waiting out
  // the remainder of the 60s interval.
  useEffect(() => {
    if (!authReady) return;
    const controller = new AbortController();
    pollRef.current(controller);
    return () => controller.abort();
  }, [authReady]);

  // Simulation triggers — see the note above the storage keys.
  useEffect(() => {
    if (isProduction()) return;
    const check = () => {
      if (localStorage.getItem(DEV_NEW_VERSION_KEY) === '1') {
        setNewVersion(prev => prev ?? { commit_sha: 'dev-simulated', commit_message: 'simulated new version' });
      }
      setDevAlertText(localStorage.getItem(DEV_ALERT_KEY));
    };
    check();
    const t = setInterval(check, DEV_POLL_MS);
    return () => clearInterval(t);
  }, []);

  const devAlert: GetWebAppAlertResponse[] = devAlertText
    ? [{ id: 'dev-alert', name: 'dev', text: devAlertText, showFrom: '', showTo: '' }]
    : [];

  return (
    <AppMessagesContext.Provider value={{ alerts: [...alerts, ...devAlert], newVersion }}>
      {children}
      <NewVersionBanner />
    </AppMessagesContext.Provider>
  );
};

export default AppMessagesProvider;
